import { XIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as Dialog from '@radix-ui/react-dialog'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { ScorecardTotalDonut } from '../../components/scorecard-total-donut.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import {
  invalidateGetRepositoryScorecards,
  useGetRepository,
  useGetRepositoryScorecards,
  useGetRepositoryThreatModel,
  usePostRepositoryScorecards,
} from '../../utils/api-client/api-hooks.ts'
import { type DimensionScore, type ScorecardsPhase } from '../../utils/api-client/api-schemas.ts'
import { formatDate, formatRelativeTime } from '../../utils/date-utils.ts'
import { joinMetaSegments, pluralize } from '../../utils/text-utils.ts'
import * as bandStyles from '../../utils/threat-model/scorecard-bands.css.ts'
import { postureTagLabel, toPostureBand } from '../../utils/threat-model/scorecard-bands.ts'
import { scorecardPostureSummary } from '../../utils/threat-model/scorecard-posture-summary.ts'
import { meanScore } from '../../utils/threat-model/scorecard-score.ts'
import { SCORECARDS_PHASE_LABELS, SCORECARDS_PHASES } from '../../utils/threat-model/scorecards-phase-labels.ts'
import { PhaseStepper } from './phase-stepper.tsx'
import { RepoRiskScorecard } from './repo-risk-scorecard.tsx'
import { useRepositoryDetailsPageParams } from './repository-details-page.tsx'
import * as styles from './scorecards-tab.css.ts'

export type ScorecardsTabProps = {
  onSwitchToThreatModel: () => unknown
}

export const ScorecardsTab: React.FC<ScorecardsTabProps> = ({ onSwitchToThreatModel }) => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const queryClient = useQueryClient()

  const { data: repository } = useGetRepository({ repositoryId })
  const { data: threatModel, isLoading: isThreatModelLoading } = useGetRepositoryThreatModel({ repositoryId })
  const { data: scorecards, isLoading: isScorecardsLoading } = useGetRepositoryScorecards({ repositoryId })

  const showGenerateError = useShowMutationError<typeof usePostRepositoryScorecards>({
    prefix: 'Failed to start scorecard generation',
  })
  const generateMutation = usePostRepositoryScorecards({
    onSuccess: () => invalidateGetRepositoryScorecards(queryClient, { repositoryId }),
    onError: showGenerateError,
  })
  const isStarting = generateMutation.isPending
  const onGenerate = useCallback(
    () => generateMutation.mutate({ repositoryId }),
    [generateMutation.mutate, repositoryId]
  )

  // Scorecard generation anchors on the threat model — the backend enforces this with a 409, and
  // we surface the same prerequisite in the UI so the user isn't guessing at the failure mode.
  const isThreatModelReady = !!threatModel?._links.content

  const [selectedDimensionId, setSelectedDimensionId] = useState<string>()

  const defaultBranch = repository?.default_branch

  if (isScorecardsLoading || isThreatModelLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading scorecards" />
      </div>
    )
  }

  // Nothing generated yet: gate on the threat-model prerequisite before offering generation.
  if (!scorecards) {
    return isThreatModelReady ? (
      <ScorecardsEmptyState onGenerate={onGenerate} isStarting={isStarting} />
    ) : (
      <ScorecardsThreatModelRequired onGenerateThreatModel={onSwitchToThreatModel} />
    )
  }

  const currentPhase = scorecards.phase
  const payload = scorecards.scorecard
  const dimensions = payload?.dimensions ?? []
  const selectedDimension = dimensions.find(d => d.dimension === selectedDimensionId)

  return (
    <div className={styles.container}>
      {match(scorecards.status)
        .with('processing', () => (
          <ScorecardsGeneratingState phase={currentPhase} requestedBy={scorecards.requestedBy} />
        ))
        .with('failed', () => (
          <ScorecardsFailedBanner
            failureReason={scorecards.failureReason}
            onRetry={onGenerate}
            isRetrying={isStarting}
            canRetry={isThreatModelReady}
          />
        ))
        .with('completed', () =>
          payload ? (
            <>
              <ScorecardsMetaRow
                generatedAt={scorecards.generatedAt}
                branch={defaultBranch}
                commitSha={scorecards.commitSha}
                requestedBy={scorecards.requestedBy}
                dimensionCount={dimensions.length}
                onRegenerate={onGenerate}
                isStarting={isStarting}
                canRegenerate={isThreatModelReady}
              />
              <ScorecardsRiskScoreBanner dimensions={dimensions} />
              <DimensionCardGrid dimensions={dimensions} onSelect={setSelectedDimensionId} />
            </>
          ) : (
            <ScorecardsNoPayloadState />
          )
        )
        .exhaustive()}
      {selectedDimension && (
        <DimensionDetailDrawer dimension={selectedDimension} onClose={() => setSelectedDimensionId(undefined)} />
      )}
    </div>
  )
}

// #region Threat model prerequisite

type ScorecardsThreatModelRequiredProps = {
  onGenerateThreatModel: () => unknown
}

/**
 * Mirrors the threat model tab's own empty state rather than the exploit-chains checklist: there is only
 * one prerequisite, and a checklist of one reads as more ceremony than the situation warrants.
 */
const ScorecardsThreatModelRequired: React.FC<ScorecardsThreatModelRequiredProps> = ({ onGenerateThreatModel }) => (
  <div className={styles.emptyState}>
    <p className={styles.emptyStateTitle}>No scorecards yet</p>
    <p className={styles.emptyStateDescription}>
      Scorecards are the result of assessing this repository against a rubric, informed by the system understanding in
      its threat model; therefore, a threat model must be generated first.
    </p>
    <DefaultButton buttonType="primary" onClick={onGenerateThreatModel}>
      Generate threat model
    </DefaultButton>
  </div>
)

// #endregion

// #region Empty / no-payload states

type ScorecardsEmptyStateProps = {
  onGenerate: () => unknown
  isStarting: boolean
}

const ScorecardsEmptyState: React.FC<ScorecardsEmptyStateProps> = ({ onGenerate, isStarting }) => (
  <div className={styles.emptyState}>
    <p className={styles.emptyStateTitle}>No scorecard yet</p>
    <p className={styles.emptyStateDescription}>
      Generate a scorecard to see this repository's security posture across ten independent dimensions. Generation
      usually takes a few minutes.
    </p>
    <DefaultButton buttonType="primary" state={isStarting ? 'loading' : 'default'} onClick={onGenerate}>
      Generate scorecard
    </DefaultButton>
  </div>
)

const ScorecardsNoPayloadState: React.FC = () => (
  <div className={styles.emptyState} data-testid="scorecards-no-payload">
    <p className={styles.emptyStateTitle}>Scorecard payload unavailable</p>
    <p className={styles.emptyStateDescription}>
      The latest run completed but produced no payload. Regenerating usually resolves this.
    </p>
  </div>
)

// #endregion

// #region Generation progress (mirrors exploit-chains / threat model)

type ScorecardsGeneratingStateProps = {
  phase: ScorecardsPhase
  requestedBy?: string
}

const ScorecardsGeneratingState: React.FC<ScorecardsGeneratingStateProps> = ({ phase, requestedBy }) => (
  <div className={styles.generatingState}>
    <PhaseStepper
      phases={SCORECARDS_PHASES}
      labels={SCORECARDS_PHASE_LABELS}
      currentPhase={phase}
      currentColor={mappedColors.primary.pulse}
      currentTextColor={mappedColors.severity.text}
    />
    <div className={styles.generatingMeta}>
      <p className={styles.generatingTitle}>
        <Spinner size="16" label="Generating scorecard" />
        Generating scorecard
      </p>
      <p className={styles.metaText}>{joinMetaSegments([requestedBy && `Requested by ${requestedBy}`])}</p>
      <p className={styles.hintText}>
        Usually takes a few minutes. You can leave this page — generation continues in the background.
      </p>
    </div>
  </div>
)

type ScorecardsFailedBannerProps = {
  failureReason: string | undefined
  onRetry: () => unknown
  isRetrying: boolean
  canRetry: boolean
}

const ScorecardsFailedBanner: React.FC<ScorecardsFailedBannerProps> = ({
  failureReason,
  onRetry,
  isRetrying,
  canRetry,
}) => (
  <div className={styles.failedBanner} role="alert">
    <div className={styles.failedBannerHeader}>
      <p className={styles.failedBannerTitle}>Scorecard generation failed</p>
      <DefaultButton
        buttonType="secondary"
        size="small"
        state={isRetrying ? 'loading' : canRetry ? 'default' : 'disabled'}
        onClick={onRetry}
      >
        Try again
      </DefaultButton>
    </div>
    {failureReason && <p className={styles.failedBannerReason}>{failureReason}</p>}
  </div>
)

type ScorecardsMetaRowProps = {
  generatedAt: string | undefined
  branch?: string
  commitSha?: string
  requestedBy?: string
  dimensionCount: number
  onRegenerate: () => unknown
  isStarting: boolean
  canRegenerate: boolean
}

const ScorecardsMetaRow: React.FC<ScorecardsMetaRowProps> = ({
  generatedAt,
  branch,
  commitSha,
  requestedBy,
  dimensionCount,
  onRegenerate,
  isStarting,
  canRegenerate,
}) => {
  const shortSha = commitSha?.slice(0, 7)
  const provenance = [branch, shortSha ? `@ ${shortSha}` : undefined].filter(Boolean).join(' ')

  return (
    <div className={styles.metaRow}>
      <p className={styles.metaText}>
        {joinMetaSegments([
          generatedAt && `Generated ${formatDate({ date: generatedAt, type: 'mm/dd/yyyy-hh:mm' })}`,
          provenance,
          `${dimensionCount} ${pluralize(dimensionCount, 'dimension', 'dimensions')}`,
          requestedBy && `requested by ${requestedBy}`,
        ])}
      </p>
      <DefaultButton
        buttonType="secondary"
        size="small"
        state={isStarting ? 'loading' : canRegenerate ? 'default' : 'disabled'}
        onClick={onRegenerate}
      >
        Regenerate
      </DefaultButton>
    </div>
  )
}

// #endregion

// #region Overview banner + dimension card grid

const RISK_SCORE_HEADING_ID = 'scorecards-risk-score-heading'

/**
 * The cumulative score and what it means, in place of the model's own system overview.
 *
 * The prose is *derived from the scores below* rather than restating the threat model — the Overview
 * sub-tab already carries `system_overview`, and repeating it here left the scorecard with a headline
 * that never referred to the cards under it.
 */
const ScorecardsRiskScoreBanner: React.FC<{ dimensions: DimensionScore[] }> = ({ dimensions }) => {
  const scores = dimensions.map(dimension => dimension.score)
  const scoredCount = scores.filter(score => Number.isFinite(score)).length

  // Named by its own heading rather than an `aria-label`, so the region and the heading do not announce
  // the same words twice.
  return (
    <section aria-labelledby={RISK_SCORE_HEADING_ID} className={styles.overviewBanner}>
      <ScorecardTotalDonut score={meanScore(scores)} scoredCount={scoredCount} totalCount={dimensions.length} />
      <div className={styles.overviewProse}>
        <h2 id={RISK_SCORE_HEADING_ID} className={styles.overviewLabel}>
          Risk score
        </h2>
        <p className={styles.overviewText}>{scorecardPostureSummary(dimensions)}</p>
      </div>
    </section>
  )
}

type DimensionCardGridProps = {
  dimensions: DimensionScore[]
  onSelect: (dimensionId: string) => unknown
}

// No dimmed variant, unlike the threat-model and prioritization content: the scorecards GET carries
// its payload inline and the spec drops it while a run is processing or after one fails, so there is
// never a previous scorecard to keep on screen behind a banner.
const DimensionCardGrid: React.FC<DimensionCardGridProps> = ({ dimensions, onSelect }) => (
  <section aria-label="Scorecard dimensions" className={styles.cardGrid} data-testid="scorecards-card-grid">
    {dimensions.map(dimension => (
      <RepoRiskScorecard key={dimension.dimension} dimension={dimension} onSelect={onSelect} />
    ))}
  </section>
)

// #endregion

// #region Detail drawer

type DimensionDetailDrawerProps = {
  dimension: DimensionScore
  onClose: () => unknown
}

const DimensionDetailDrawer: React.FC<DimensionDetailDrawerProps> = ({ dimension, onClose }) => {
  const band = toPostureBand(dimension.score)
  const confidencePercent = Math.round(dimension.confidence * 100)

  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Portal>
        <Dialog.Content
          className={styles.drawerContent}
          onEscapeKeyDown={onClose}
          onPointerDownOutside={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
        >
          <Dialog.Close asChild className={styles.drawerCloseButton}>
            <button aria-label="Close" onClick={onClose}>
              <XIcon size={24} weight="light" />
            </button>
          </Dialog.Close>
          <div className={styles.drawerHeader}>
            <Dialog.Title className={styles.drawerTitle}>{dimension.displayName}</Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.drawerSubtitle}>{dimension.description}</p>
            </Dialog.Description>
            <p className={styles.drawerMeta}>
              <span className={bandStyles.scoreTag({ band })}>{postureTagLabel(dimension.score)}</span>
              {joinMetaSegments([
                `${confidencePercent}% confidence`,
                `rubric ${dimension.rubricVersion}`,
                `scored ${formatRelativeTime(dimension.generatedAt)}`,
              ])}
            </p>
          </div>
          <p className={styles.drawerRationale}>{dimension.rationale}</p>
          {/* An h3: Radix renders the drawer's own `Dialog.Title` as an h2, and this names a section within it. */}
          <h3 className={styles.drawerSectionTitle}>Rubric answers</h3>
          <ul className={styles.answersList}>
            {dimension.answers.map(answer => (
              <li key={answer.id} className={styles.answerRow}>
                <div className={styles.answerHeader}>
                  <p className={styles.answerQuestion}>{answer.question}</p>
                  <span className={styles.answerTag({ value: answer.answer })} data-answer={answer.answer}>
                    {answer.answer}
                  </span>
                </div>
                {(answer.rationale || answer.evidence.length > 0) && (
                  <div className={styles.answerBody}>
                    {answer.rationale && <p className={styles.answerRationale}>{answer.rationale}</p>}
                    {answer.evidence.length > 0 && (
                      <ul className={styles.evidenceList}>
                        {answer.evidence.map((evidence, index) => (
                          <li key={`${answer.id}-${index}`} className={styles.evidenceRow}>
                            <p className={styles.evidenceLocation}>{evidence.location}</p>
                            {evidence.note && <p className={styles.evidenceNote}>{evidence.note}</p>}
                            {evidence.excerpt && <pre className={styles.evidenceExcerpt}>{evidence.excerpt}</pre>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// #endregion
